import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  CalendarIcon,
  UserGroupIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { workshopAPI } from '../../services/api';
import { useToast } from '../Toast';

const WorkshopForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [workshop, setWorkshop] = useState({
    name: '',
    fees: '',
    is_paid: true,
    start_date: '',
    end_date: '',
    description: '',
    whatsapp_group_link: '',
    max_participants: ''
  });

  useEffect(() => {
    if (id) {
      fetchWorkshop();
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchWorkshop = async () => {
    try {
      setLoading(true);
      const response = await workshopAPI.getById(id);
      const workshopData = response.data;
      
      // Format dates for input fields
      setWorkshop({
        name: workshopData.name,
        fees: workshopData.fees?.toString() || '',
        is_paid: workshopData.is_paid,
        start_date: new Date(workshopData.start_date).toISOString().split('T')[0],
        end_date: new Date(workshopData.end_date).toISOString().split('T')[0],
        description: workshopData.description || '',
        whatsapp_group_link: workshopData.whatsapp_group_link || '',
        max_participants: workshopData.max_participants?.toString() || ''
      });
    } catch (error) {
      console.error('Error fetching workshop:', error);
      showToast('Failed to load workshop details', 'error');
      navigate('/workshops');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setWorkshop(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!workshop.name.trim()) {
      showToast('Workshop name is required', 'error');
      return false;
    }

    if (workshop.is_paid && (!workshop.fees || parseFloat(workshop.fees) <= 0)) {
      showToast('Please enter a valid fee amount for paid workshops', 'error');
      return false;
    }

    if (!workshop.start_date) {
      showToast('Start date is required', 'error');
      return false;
    }

    if (!workshop.end_date) {
      showToast('End date is required', 'error');
      return false;
    }

    if (new Date(workshop.start_date) > new Date(workshop.end_date)) {
      showToast('End date cannot be before start date', 'error');
      return false;
    }

    if (workshop.max_participants && parseInt(workshop.max_participants) <= 0) {
      showToast('Maximum participants must be a positive number', 'error');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      
      const workshopData = {
        name: workshop.name.trim(),
        fees: workshop.is_paid ? parseFloat(workshop.fees) : null,
        is_paid: workshop.is_paid,
        start_date: new Date(workshop.start_date).toISOString(),
        end_date: new Date(workshop.end_date).toISOString(),
        description: workshop.description.trim() || null,
        whatsapp_group_link: workshop.whatsapp_group_link.trim() || null,
        max_participants: workshop.max_participants ? parseInt(workshop.max_participants) : null
      };

      if (id) {
        await workshopAPI.update(id, workshopData);
        showToast('Workshop updated successfully', 'success');
      } else {
        await workshopAPI.create(workshopData);
        showToast('Workshop created successfully', 'success');
      }
      
      navigate('/workshops');
    } catch (error) {
      console.error('Error saving workshop:', error);
      showToast(error.response?.data?.detail || 'Failed to save workshop', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/workshops')}
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">
              {id ? 'Edit Workshop' : 'Create Workshop'}
            </h1>
            <p className="text-gray-400 mt-1">
              {id ? 'Update workshop details' : 'Set up a new workshop for student registration'}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div>
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <DocumentTextIcon className="h-5 w-5 text-blue-400" />
              Basic Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Workshop Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={workshop.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                  placeholder="e.g., Python for Beginners"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Workshop Type
                </label>
                <div className="flex items-center space-x-3">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="is_paid"
                      checked={workshop.is_paid}
                      onChange={(e) => setWorkshop(prev => ({ ...prev, is_paid: e.target.checked }))}
                      className="rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                    />
                    <span className="text-sm text-gray-300">Paid Workshop</span>
                  </label>
                </div>
              </div>
            </div>
            
            {workshop.is_paid && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Workshop Fees (₹) *
                </label>
                <input
                  type="number"
                  name="fees"
                  value={workshop.fees}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                  placeholder="1500"
                  required
                />
              </div>
            )}
          </div>

          {/* Duration */}
          <div>
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-blue-400" />
              Duration
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Start Date *
                </label>
                <input
                  type="date"
                  name="start_date"
                  value={workshop.start_date}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  End Date *
                </label>
                <input
                  type="date"
                  name="end_date"
                  value={workshop.end_date}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                  required
                />
              </div>
            </div>
          </div>

          {/* Additional Details */}
          <div>
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <UserGroupIcon className="h-5 w-5 text-blue-400" />
              Additional Details
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Maximum Participants
                </label>
                <input
                  type="number"
                  name="max_participants"
                  value={workshop.max_participants}
                  onChange={handleInputChange}
                  min="1"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                  placeholder="30 (optional)"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty for unlimited participants</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  WhatsApp Group Link
                </label>
                <input
                  type="url"
                  name="whatsapp_group_link"
                  value={workshop.whatsapp_group_link}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                  placeholder="https://chat.whatsapp.com/..."
                />
                <p className="text-xs text-gray-500 mt-1">Optional WhatsApp group link for participants</p>
              </div>
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={workshop.description}
                onChange={handleInputChange}
                rows="4"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                placeholder="Describe what students will learn in this workshop..."
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-6 border-t border-gray-600">
            <div className="flex items-center justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate('/workshops')}
                className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium transition-colors"
              >
                {submitting ? 'Saving...' : (id ? 'Update Workshop' : 'Create Workshop')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WorkshopForm; 